import { Text } from '@chakra-ui/react'

import { COLLEGE_PREP_WORLD_SLUG } from '@/client/services/worlds'

import ShwLink from '@/components/ShwLink'
import type { FormBlock, FormValues } from '@/components/UI/Form/types'

import {
  ACKNOWLEDGEMENTS_INPUT_NAMES,
  type CAWConsentDetails,
  DATA_CONSENT_INPUT_NAME,
  FEDERAL_WORK_STUDY_INPUT_NAME,
  FOUR_YEAR_COLLEGE_INPUT_NAME,
} from './constants'

// When you update `getCAWConsentFormInputs`, you may also need to update this function to parse it into an understandable schema
export function getCAWConsentResponses({
  formValues,
}: {
  formValues: FormValues
}): CAWConsentDetails {
  return {
    acknowledgements: ACKNOWLEDGEMENTS_INPUT_NAMES.filter(key => {
      const formValue = formValues[key]
      return (
        formValue === 'yes' ||
        (Array.isArray(formValue) && formValue.length > 0 && formValue[0] === 'yes')
      )
    }),
  }
}

export function getCAWConsentFormInputs({ type }: { type: 'learner' | 'tutor' }): FormBlock[] {
  const fourYearCollegeConfirmation: FormBlock = {
    type: 'multi-checkbox',
    name: FOUR_YEAR_COLLEGE_INPUT_NAME,
    label: 'Confirm your enrollment in a 4-year university or college',
    sublabel: (
      <Text color="fg.muted">
        At the moment, only students who have been admitted to, are enrolled in, or have graduated
        from a 4-year United States university or college are eligible to tutor a College Admissions
        Workshop. If you are enrolled in a 2-year or international institution, we encourage you to
        tutor in our <ShwLink href={COLLEGE_PREP_WORLD_SLUG}>College Prep</ShwLink> subworld.
      </Text>
    ),
    options: [
      {
        label:
          'I have been admitted to, am enrolled in, or have graduated from a 4-year university or college in the United States',
        value: 'yes',
      },
    ],
    required: true,
    size: 6,
  }

  const federalWorkStudyConfirmation: FormBlock = {
    type: 'radio',
    name: FEDERAL_WORK_STUDY_INPUT_NAME,
    label: 'Will you be seeking Federal Work Study compensation for this tutoring position?',
    sublabel: (
      <Text color="fg.muted">
        When I select yes, I acknowledge that:
        <ul style={{ paddingLeft: '24px' }}>
          <li>I cannot begin paid work until my university approves my FWS employment.</li>
          <li>This form does not guarantee FWS compensation.</li>
          <li>
            Any work I do before official FWS approval will be considered unpaid volunteer work.
          </li>
        </ul>
      </Text>
    ),
    options: [
      {
        label: 'Yes, I am seeking Federal Work Study compensation.',
        value: 'yes',
      },
      {
        label: 'No, I am not seeking FWS compensation',
        value: 'no',
      },
    ],
    required: false,
    size: 6,
  }

  const dataConsent: FormBlock = {
    type: 'multi-checkbox',
    name: DATA_CONSENT_INPUT_NAME,
    label: 'Data Sharing',
    sublabel: (
      <Text color="fg.muted">
        As a part of this program, we may share some of this workshop data (e.g., survey results,
        recordings, etc.) with the Coalition for College and/or BigFuture to improve the quality of
        this tutoring program. Coalition and BigFuture may also share aggregated and/or
        de-identified survey data and other data with donors and other partner institutions to
        encourage the ongoing development of the College Admission Workshop program.
      </Text>
    ),
    options: [
      {
        label:
          'I acknowledge that some of my Schoolhouse data may be shared with the Coalition and BigFuture',
        value: 'yes',
      },
    ],
    required: true,
    size: 6,
  }

  return type === 'learner'
    ? [dataConsent]
    : [fourYearCollegeConfirmation, dataConsent, federalWorkStudyConfirmation]
}
